import { getSetting, useSetting } from '@/core/settings';

// Who the numbers are about and what they're aiming for. Synced (encrypted) as one setting.

export type Units = 'lb' | 'kg';

export interface Profile {
  units: Units;
  heightCm?: number;
  birthday?: string;
  sex?: 'male' | 'female';
  activity?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very';
  goal?: 'lose' | 'maintain' | 'gain';
  kcalGoal: number;
  /** grams per day; unset = worked out from calories */
  proteinGoal?: number;
  carbsGoal?: number;
  fatGoal?: number;
  fiberGoal: number;
  weightGoalKg?: number;
  sleepGoalH: number;
  stepsGoal: number;
  /**
   * Where you're based. Nights nothing else places are put here, and Sleep can show a home
   * clock. '' = none: unknown nights get a !, and Sleep compares nights with your body clock.
   */
  homeZone?: string;
  /** what to call home's zone ("Fort Myers" rather than "New York"); '' = the zone's own city */
  homeName?: string;
  /** foods the AI never suggests */
  hatedFoods: string[];
}

export const PROFILE_KEY = 'profile';

export const DEFAULT_HOME = { zone: 'America/New_York', name: 'Fort Myers' };

export const DEFAULT_PROFILE: Profile = {
  units: 'lb',
  kcalGoal: 2200,
  fiberGoal: 30,
  sleepGoalH: 8,
  stepsGoal: 8000,
  homeZone: DEFAULT_HOME.zone,
  homeName: DEFAULT_HOME.name,
  hatedFoods: [],
};

export function getProfile(): Profile {
  return { ...DEFAULT_PROFILE, ...getSetting<Partial<Profile>>(PROFILE_KEY, {}) };
}

export function useProfile(): [Profile, (patch: Partial<Profile>) => void] {
  const [raw, set] = useSetting<Partial<Profile>>(PROFILE_KEY, {});
  const p = { ...DEFAULT_PROFILE, ...raw };
  return [p, (patch) => set({ ...raw, ...patch })];
}

/** Protein / carbs / fat targets in grams: the ones set, else 25% / 45% / 30% of calories. */
export function macroGoals(p: Profile): { protein: number; carbs: number; fat: number } {
  return {
    protein: p.proteinGoal ?? Math.round((p.kcalGoal * 0.25) / 4),
    carbs: p.carbsGoal ?? Math.round((p.kcalGoal * 0.45) / 4),
    fat: p.fatGoal ?? Math.round((p.kcalGoal * 0.3) / 9),
  };
}

// ------------------------------------------------------------------ units

export const KG_PER_LB = 0.45359237;

export function toUnit(kg: number, u: Units): number {
  return u === 'lb' ? kg / KG_PER_LB : kg;
}

export function fromUnit(v: number, u: Units): number {
  return u === 'lb' ? v * KG_PER_LB : v;
}

/** "128.4 lb" / "58.2 kg" */
export function fmtWeight(kg: number, u: Units, digits = 1): string {
  return `${toUnit(kg, u).toFixed(digits)} ${u}`;
}

/** "+1.2 lb" / "−0.4 kg" */
export function fmtWeightDelta(kg: number, u: Units): string {
  const v = Math.round(toUnit(kg, u) * 10) / 10;
  return `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(v).toFixed(1)} ${u}`;
}

export function age(birthday: string | undefined, now = new Date()): number | undefined {
  if (!birthday) return undefined;
  const b = new Date(`${birthday}T12:00:00`);
  let a = now.getFullYear() - b.getFullYear();
  if (
    now.getMonth() < b.getMonth() ||
    (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())
  )
    a--;
  return a;
}

const ACTIVITY: Record<NonNullable<Profile['activity']>, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very: 1.9,
};

/** Mifflin-St Jeor estimate of daily calories to keep weight steady. */
export function maintenanceKcal(p: Profile, weightKg: number | undefined): number | undefined {
  const a = age(p.birthday);
  if (!weightKg || !p.heightCm || a === undefined || !p.sex) return undefined;
  const bmr = 10 * weightKg + 6.25 * p.heightCm - 5 * a + (p.sex === 'male' ? 5 : -161);
  return Math.round(bmr * ACTIVITY[p.activity ?? 'light']);
}

/** "7h 42m" from seconds. */
export function fmtHM(seconds: number | undefined): string {
  if (seconds === undefined || !Number.isFinite(seconds)) return '—';
  const m = Math.round(seconds / 60);
  const h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}

export function fmtInt(n: number | undefined): string {
  return n === undefined || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString();
}
